import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  CircularProgress,
  styled,
} from '@material-ui/core';
import { datadogRum } from '@datadog/browser-rum';

const CenteredContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
});

// Parameters that contain the destination URL (not forwarded)
// When iterating through URL params, these will be excluded
const URL_PARAMS = ['_p', '_fallback_redirect'];

const SingularLanding: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    // Get destination URL: _p takes priority over _fallback_redirect
    const destinationParam = searchParams.get('_p') || searchParams.get('_fallback_redirect');

    datadogRum.addAction('[SingularLanding] page loaded', {
      hasP: Boolean(searchParams.get('_p')),
      hasFallback: Boolean(searchParams.get('_fallback_redirect')),
      hasDestination: Boolean(destinationParam),
    });

    const redirect = (url: string, outcome: string) => {
      datadogRum.addAction('[SingularLanding] redirecting', { outcome });
      // Give Singular SDK time to process the link before redirecting
      const redirectTimer = setTimeout(() => {
        window.location.href = url;
      }, 500);

      return () => clearTimeout(redirectTimer);
    };

    if (destinationParam) {
      try {
        // Decode and parse the destination URL
        // as this could have params itself.
        const decodedUrl = decodeURIComponent(destinationParam);
        const destinationUrl = new URL(decodedUrl);

        // Append any extra params (excluding _p and _fallback_redirect)
        searchParams.forEach((value, key) => {
          if (!URL_PARAMS.includes(key) && value) {
            destinationUrl.searchParams.set(key, value);
          }
        });

        return redirect(destinationUrl.toString(), 'destination');
      } catch {
        // Invalid URL, fallback to root
        return redirect('/', 'error_invalid_url');
      }
    }

    // No destination provided, fallback to root
    return redirect('/', 'no_destination');
  }, [location.search]);

  // Show loading indicator while processing
  // This might work so fast that the user may never see it,
  // but it's here for the JIC.
  return (
    <CenteredContainer maxWidth="sm">
      <CircularProgress size="6rem" thickness={3} />
    </CenteredContainer>
  );
};

export default SingularLanding;
